// URL sanitization for everything the portal reports.
//
// The booking flow carries clinical and identifying values in the query string — the consultation
// reason and symptom note, the schedule phone number, the pharmacy ZIP and city, the requested
// product list. None of them may reach an analytics destination, so no URL is ever reported as-is:
// it goes through here first, where record ids in the path are masked and every query parameter
// except the marketing ones is dropped.

import { MARKETING_PARAMS } from "./attribution";

const UUID_PATTERN = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

/**
 * Reduces a URL to a shape safe to report: record ids in the path become {id}, and every query
 * parameter except the marketing ones is dropped, so schedule/encounter/member ids never leak.
 */
export const sanitizePath = (pathname = "", search = "") => {
    const path = (pathname || "/")
        .split("/")
        .map((segment) => {
            if (!segment) return segment;
            if (UUID_PATTERN.test(segment)) return "{id}";
            if (/^\d+$/.test(segment)) return "{id}";
            return segment;
        })
        .join("/");

    const kept = new URLSearchParams();
    try {
        const params = new URLSearchParams(search || "");
        MARKETING_PARAMS.forEach((name) => {
            const value = params.get(name);
            if (value && value.trim()) kept.set(name, value.trim());
        });
    } catch (e) {
        // A malformed query string simply contributes nothing.
    }

    const query = kept.toString();
    return query ? `${path}?${query}` : path;
};

/**
 * The absolute form of sanitizePath. GA4 reports a full URL rather than a path, and left to itself
 * it reads document.location.href — the raw address bar, query string and all. Publishing this to
 * the data layer gives the container a clean value to bind the tag's page_location field to.
 */
export const sanitizeLocation = (pathname = "", search = "") => {
    const path = sanitizePath(pathname, search);

    try {
        return `${window.location.origin}${path}`;
    } catch (e) {
        return path;
    }
};

/**
 * A referrer from our own pages is sanitized — it is a previous step of the booking flow and carries
 * the same values the address bar does. A referrer from anywhere else passes through untouched, so
 * the marketing site a patient arrived from still reaches attribution reporting.
 */
export const sanitizeReferrer = (referrer) => {
    try {
        if (!referrer) return undefined;

        const u = new URL(referrer);
        if (u.origin !== window.location.origin) return referrer;

        return sanitizeLocation(u.pathname, u.search);
    } catch (e) {
        return undefined;
    }
};
